import { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH_URL } from 'src/helpers/path'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import { PartyCountFormState, UsePartyCountHookResult } from './types'
import { QuizResponse } from 'src/types'

const INITIAL_FORM_STATE: PartyCountFormState = {
  isSubmitting: false,
  error: null,
}

export const usePartyCount = (initialValue = ''): UsePartyCountHookResult => {
  const [partyCount, setPartyCount] = useState(initialValue)
  const [formState, setFormState] = useState<PartyCountFormState>(INITIAL_FORM_STATE)
  const history = useHistory()

  // Check for collectionId parameter and save it to collectionsQuizEntry
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in PartyCountPage, saving to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [])

  const validatePartyCount = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter the number of people in your wedding party'
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return 'Please enter a valid number'
    }

    if (!Number.isInteger(numValue)) {
      return 'Please enter a whole number'
    }

    if (numValue <= 0) {
      return 'Wedding party count must be greater than 0'
    }

    return null
  }

  const handlePartyCountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      // Only allow numbers and backspace
      if (!/^\d*$/.test(value) && value !== '') {
        return
      }
      setPartyCount(value)
      if (formState.error) {
        setFormState((prev) => ({ ...prev, error: null }))
      }
    },
    [formState.error]
  )

  const handleSubmit = useCallback(async (): Promise<void> => {
    try {
      setFormState((prev) => ({ ...prev, isSubmitting: true, error: null }))

      const validationError = validatePartyCount(partyCount)
      if (validationError) {
        setFormState((prev) => ({ ...prev, error: validationError, isSubmitting: false }))
        return
      }

      // Save party count to localStorage
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

      const quizResponses = collectionsQuizEntry.quizResponses || []
      const partyCountIndex = quizResponses.findIndex((item: QuizResponse) => item.key === 'PARTY_COUNT')
      const updatedQuizResponses =
        partyCountIndex > -1
          ? quizResponses.map((item: QuizResponse, index: number) =>
              index === partyCountIndex ? { ...item, values: partyCount } : item
            )
          : [...quizResponses, { key: 'PARTY_COUNT', values: partyCount }]

      collectionsQuizEntry.quizResponses = updatedQuizResponses

      // Ensure collectionId is saved if present
      const params = getQueryParameters()
      const collectionId = params.get('collectionId') || collectionsQuizEntry.collectionId
      if (collectionId) {
        collectionsQuizEntry.collectionId = collectionId
      }

      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

      // Use preserveUrlParams to maintain query parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_TABLE_COUNT)
      history.push(nextUrl)
    } catch (error) {
      setFormState((prev) => ({
        ...prev,
        error: 'An error occurred while saving. Please try again.',
        isSubmitting: false,
      }))
    }
  }, [partyCount, history])

  const handleSkip = useCallback(() => {
    try {
      // Save empty party count to localStorage
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

      const quizResponses = collectionsQuizEntry.quizResponses || []
      const partyCountIndex = quizResponses.findIndex((item: QuizResponse) => item.key === 'PARTY_COUNT')
      const updatedQuizResponses =
        partyCountIndex > -1
          ? quizResponses.map((item: QuizResponse, index: number) =>
              index === partyCountIndex ? { ...item, values: '' } : item
            )
          : [...quizResponses, { key: 'PARTY_COUNT', values: '' }]

      collectionsQuizEntry.quizResponses = updatedQuizResponses

      // Ensure collectionId is saved if present
      const params = getQueryParameters()
      const collectionId = params.get('collectionId') || collectionsQuizEntry.collectionId
      if (collectionId) {
        collectionsQuizEntry.collectionId = collectionId
      }

      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

      // Use preserveUrlParams to maintain query parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_TABLE_COUNT)
      history.push(nextUrl)
    } catch (error) {
      console.warn('Failed to skip party count in localStorage:', error)
    }
  }, [history])

  return {
    formState,
    partyCount,
    handlePartyCountChange,
    handleSubmit,
    handleSkip,
    error: formState.error,
  }
}
